// F14: ③-1 画像生成プロンプト — cast（主人公/プレイアブル/NPC/敵）ごとに画像生成用プロンプトファイルを作る。
// 人型は「A 字型全身」、非人型（モンスター・動物）は「そのままのイメージ」の定型文をコードで合成する。
// 画像生成そのもの（Gemini 等）と 3D 化（Meshy 等）は手動前提。ファイルは image-prompts/ にいつでも閲覧できる形で残す。
import { writeFileSync, mkdirSync } from 'node:fs';
import path from 'node:path';
import { callLlmJson } from './llm-client.mjs';
import { pipelineConfig, fillTemplate, str, asArray } from './story-shared.mjs';
import { projectPaths, requireAnalysis, readProjectMeta, recordStep, addUsage } from './story-project.mjs';
import { readJsonIfExists, writeJson } from './json-utils.mjs';
import { auditLog } from './logger.mjs';
import { requireGameDir } from './games.mjs';
import {
    applyVisualProfilesToCharacters, normalizeVisualProfile, visualProfileJson,
} from './character-visual-profile.mjs';

export const ROLE_LABEL = {
    protagonist: '主人公',
    partner: 'プレイアブル（相方）',
    npc: 'NPC',
    enemy: '敵',
};

/**
 * ポーズ種別の決定（2026-07-06 変更・計画書§16.1(3)）:
 * - aPose  = プレイアブルと人型敵。リギング＋アニメーションを付けるため
 * - scene  = 人型 NPC。静的モデルのまま配置されるため、物語シーンに合った自然な姿
 *            （全員 A/T 字型で街に立っていると不自然、というユーザー指摘による）
 * - natural = 非人型（モンスター・動物）。従来どおりそのままのイメージ
 */
export function poseKindFor(cast) {
    if (!cast.humanoid) return 'natural';
    return cast.role === 'protagonist' || cast.role === 'partner' || cast.role === 'enemy' ? 'aPose' : 'scene';
}

const POSE_SUFFIX_KEY = {
    aPose: ['aPoseSuffixEn', 'aPoseSuffixJa'],
    scene: ['scenePoseSuffixEn', 'scenePoseSuffixJa'],
    natural: ['naturalSuffixEn', 'naturalSuffixJa'],
};

export const POSE_LABEL = { aPose: 'A字型', scene: '自然体（シーン）', natural: '自然体' };

/** cast の役割に応じた推奨 GLB 配置先（説明つき全文と、実在チェック用の先頭パス）。 */
/**
 * キャストの性別を導出する（リオ「A young man」性別誤訳バグの恒久対策・2026-07-07 §16.10）。
 * ①プレイアブルは解析の game.characters.boy/girl の id 対応が確定値
 * ②その他は appearance/personality の日本語キーワードから推定 ③不明なら null（LLM に明示を指示）。
 */
export function genderFor(cast, game = {}) {
    if (game.characters?.girl?.id === cast.id) return '女性';
    if (game.characters?.boy?.id === cast.id) return '男性';
    if (cast.humanoid === false) return null; // 非人型は性別指定しない
    const text = `${cast.appearance ?? ''} ${cast.personality ?? ''}`;
    if (/少女|女の子|娘|女性|婦人|老婆|お婆|媼|巫女|姫|乙女|彼女/.test(text)) return '女性';
    if (/少年|男の子|青年|男性|老人|爺|翁|漢|髭の男|彼(?!女)/.test(text)) return '男性';
    return null;
}

/** 確定性別と英語プロンプトの矛盾を検知する（女性なのに man/He 等・男性なのに woman/She 等）。 */
export function genderMismatch(gender, subjectEn) {
    const male = /\b(man|boy|male|he|him|his)\b/i.test(subjectEn);
    const female = /\b(woman|girl|female|lady|she|her)\b/i.test(subjectEn);
    if (gender === '女性') return male && !female;
    if (gender === '男性') return female && !male;
    return false;
}

export function glbTargetFor(cast, gameId) {
    const cfg = pipelineConfig().imagePrompts.glbTargets;
    const key = cast.role === 'protagonist' || cast.role === 'partner' ? 'playable' : cast.role === 'enemy' ? 'enemy' : 'npc';
    const text = fillTemplate(cfg[key], { gameId, castId: cast.id });
    const firstPath = str(text.split(/[（(]/)[0]);
    return { text, firstPath };
}

/**
 * 画像生成プロンプトファイルを生成する。
 * @param {object} opts { castIds?: 対象 cast id の配列（省略時は全 cast）, onLog }
 */
/** プロンプト index のエントリ差し替え。生成済み画像の情報（image* フィールド）は保全する。 */
export function mergePromptEntries(oldEntries, newEntries) {
    const prev = new Map((oldEntries ?? []).map((e) => [e.castId, e]));
    const replaced = new Set(newEntries.map((e) => e.castId));
    const withImages = newEntries.map((e) => {
        const old = prev.get(e.castId);
        if (!old) return e;
        const imageFields = Object.fromEntries(Object.entries(old).filter(([k]) => k.startsWith('image')));
        return { ...imageFields, ...e };
    });
    return [...(oldEntries ?? []).filter((e) => !replaced.has(e.castId)), ...withImages]
        .sort((a, b) => String(a.file).localeCompare(String(b.file)));
}

export async function runImagePrompts(projectId, { castIds = null, onLog = () => {}, signal = null } = {}) {
    const analysis = requireAnalysis(projectId);
    const cfg = pipelineConfig().imagePrompts;
    const p = projectPaths(projectId);
    const targets = analysis.cast.filter((c) => !castIds || castIds.includes(c.id));
    if (targets.length === 0) throw new Error('対象キャストがありません（analysis.json の cast、または castIds の指定を確認してください）。');
    mkdirSync(p.imagePromptsDir, { recursive: true });

    onLog(`画像生成プロンプトを ${targets.length}体分作成します（プレイアブル=A字型、人型NPC/敵=シーンの自然な姿、非人型=自然体）`);
    const subjects = new Map();
    const castLogs = Object.fromEntries(targets.map((cast) => [cast.id, []]));
    const batchSize = cfg.batchSize ?? 6;
    for (let i = 0; i < targets.length; i += batchSize) {
        signal?.throwIfAborted(); // 中断チェック（バッチ境界。md/index は全 LLM 完了後に書くのでここで止めれば未保存＝安全）
        const batch = targets.slice(i, i + batchSize);
        // 1回の OpenRouter 呼び出しで複数キャラを生成するため、その呼び出しログを同じバッチの
        // 各キャラへ紐づける。後処理の区切り行解析に依存せず、個別再生成・一括実行で共通保存できる。
        const batchLines = [];
        const batchLog = (line) => {
            batchLines.push(String(line));
            onLog(line);
        };
        const castList = JSON.stringify(
            batch.map((c) => ({
                id: c.id,
                name: c.name.ja,
                role: ROLE_LABEL[c.role] ?? c.role,
                humanoid: c.humanoid,
                gender: genderFor(c, analysis.game) ?? '不明（appearance と物語から推測し、subjectEn で必ず明示すること）',
                appearance: c.appearance,
                personality: c.personality,
            })),
            null,
            1,
        );
        const r = await callLlmJson('imagePrompt', { system: cfg.system, prompt: fillTemplate(cfg.prompt, { castList }), onLog: batchLog });
        addUsage(projectId, r.usage, r.calls);
        for (const item of asArray(r.json?.prompts)) {
            if (str(item?.id)) subjects.set(str(item.id), item);
        }
        // 検算ガード: 性別が確定しているキャストの英訳が矛盾していないか（黙って通さない。§16.10）
        for (const c of batch) {
            const gender = genderFor(c, analysis.game);
            const en = str(subjects.get(c.id)?.subjectEn);
            if (gender && en && genderMismatch(gender, en)) {
                batchLog(`  ⚠ ${c.name.ja}（${c.id}）: 解析では「${gender}」ですが、英語プロンプトに矛盾する語が含まれています。内容を確認し、必要なら再生成してください → ${en.slice(0, 90)}…`);
            }
        }
        batchLog(`  バッチ ${Math.floor(i / batchSize) + 1}: ${batch.map((c) => c.id).join(', ')} 完了`);
        for (const cast of batch) castLogs[cast.id].push(...batchLines);
    }

    const generatedAt = new Date().toISOString();
    const index = readJsonIfExists(p.imagePromptsIndex) ?? { formatVersion: '1.0', prompts: [] };
    // 推奨 GLB 配置先はリンク済みゲームの gameId を優先する。再解析すると analysis.game.gameId は
    // ランダム接尾辞が再付与されて変わるため、解析時点の値で焼くと手動配置フローが誤誘導される
    const glbGameId = readProjectMeta(projectId).gameId ?? analysis.game.gameId;
    index.gameId = glbGameId;
    const newEntries = [];
    const profilesById = new Map();
    for (const cast of targets) {
        const orderNo = Math.max(0, analysis.cast.findIndex((c) => c.id === cast.id)) + 1;
        const fileName = `${String(orderNo).padStart(2, '0')}-${cast.role}-${cast.id}.md`;
        const subject = subjects.get(cast.id);
        const subjectEn = str(subject?.subjectEn) || `Fantasy RPG ${cast.role === 'enemy' ? 'creature' : 'character'} "${cast.name.en}". ${cast.appearance || 'Design freely to match the story.'}`;
        const subjectJa = str(subject?.subjectJa) || cast.appearance || `${cast.name.ja}（原文に外見描写なし。作品の雰囲気に合わせてデザイン）`;
        const visualProfile = normalizeVisualProfile(cast, subject?.visualProfile, cfg.visualProfile);
        profilesById.set(cast.id, visualProfile);
        const glb = glbTargetFor(cast, glbGameId);
        const pose = poseKindFor(cast);
        const md = buildPromptMd({ cast, subjectEn, subjectJa, visualProfile, cfg, glb, generatedAt, pose });
        writeFileSync(path.join(p.imagePromptsDir, fileName), md, 'utf-8');
        newEntries.push({
            castId: cast.id,
            role: cast.role,
            nameJa: cast.name.ja,
            humanoid: cast.humanoid,
            pose,
            file: fileName,
            glbTarget: glb.text,
            glbPath: glb.firstPath,
            promptEn: composePrompt(subjectEn, cfg[POSE_SUFFIX_KEY[pose][0]]),
            visualProfile,
            generatedAt,
        });
    }
    // 個別再生成に対応: 対象分だけ差し替え、他はそのまま残す。
    // 差し替え時も画像メタデータ（imageFile 等の image* フィールド）は引き継ぐ
    //（プロンプトの再生成で「画像: 未生成」へ戻ってしまう不具合の修正・2026-07-11）
    index.prompts = mergePromptEntries(index.prompts, newEntries);
    writeJson(p.imagePromptsIndex, index);
    // プレイアブルは、標準身長でリギングした GLB を最終身長へ等方スケールする。
    // コライダーと頭上ラベルも同じプロファイル由来の値へ同期する。
    let syncedCharacters = 0;
    try {
        const gameDir = requireGameDir(glbGameId);
        const charactersPath = path.join(gameDir, 'maps/_shared/characters.json');
        const charactersDocument = readJsonIfExists(charactersPath);
        syncedCharacters = applyVisualProfilesToCharacters(
            charactersDocument,
            new Map(analysis.cast.map((cast) => [cast.id, cast])),
            profilesById,
        );
        if (syncedCharacters > 0) writeJson(charactersPath, charactersDocument);
    } catch (error) {
        onLog(`  ⚠ 見た目プロファイルのキャラ設定同期をスキップ: ${error.message}`);
    }
    recordStep(projectId, 'imagePrompts', { ok: true, generated: newEntries.length, total: index.prompts.length });
    auditLog('story-image-prompts', { projectId, generated: newEntries.length, syncedCharacters });
    onLog(`✔ 画像プロンプト ${newEntries.length}件と見た目プロファイルを image-prompts/ に保存しました（キャラ設定同期 ${syncedCharacters}体）`);
    // ジョブ結果にだけキャラ別ログを添付する（index.json の成果物スキーマには保存しない）。
    return { ...index, castLogs };
}

const composePrompt = (subject, suffix) => `${subject.replace(/[。.\s]+$/, '')}. ${suffix}`;

function buildPromptMd({ cast, subjectEn, subjectJa, visualProfile, cfg, glb, generatedAt, pose }) {
    const poseNote = {
        aPose: 'A 字型（プレイアブル/人型敵＝3D モデリング・自動リギング向け）',
        scene: '物語シーンの自然な姿（静的モデルとして配置するため A/T 字型にしない）',
        natural: '自然体（人型でないため A 字型にしない。そのままのイメージ）',
    }[pose];
    const [suffixEnKey, suffixJaKey] = POSE_SUFFIX_KEY[pose];
    return [
        `# 画像生成プロンプト: ${cast.name.ja}（${cast.id}）`,
        '',
        `- 区分: ${ROLE_LABEL[cast.role] ?? cast.role}`,
        `- 人型: ${cast.humanoid ? 'はい' : 'いいえ'} → ポーズ指定: ${poseNote}`,
        `- 元描写: ${cast.appearance || '（原文に描写なし）'}`,
        `- 性格・口調: ${cast.personality || '—'}`,
        `- 生成日時: ${generatedAt}`,
        '',
        '## 見た目プロファイル（3D化・ゲーム配置用）',
        '',
        '```json',
        visualProfileJson(visualProfile),
        '```',
        '',
        '- `heightMeters`: 小説の描写から決定した最終身長',
        '- `riggingHeightMeters`: Meshy で骨格を安定生成するための共通標準身長',
        '- `game.scaleMode=uniform`: 骨・メッシュ・アニメの相対関係を保つ等方スケール',
        '',
        '## 画像生成プロンプト（英語・推奨。Gemini / Imagen 等へ貼り付け）',
        '',
        '```text',
        composePrompt(subjectEn, cfg[suffixEnKey]),
        '```',
        '',
        '## 画像生成プロンプト（日本語）',
        '',
        '```text',
        composePrompt(subjectJa, cfg[suffixJaKey]),
        '```',
        '',
        '## ネガティブプロンプト',
        '',
        '```text',
        cfg.negativePrompt,
        '```',
        '',
        '## 3D モデル化メモ（Meshy 等で手動作成）',
        '',
        `- 推奨 GLB 配置先: \`${glb.firstPath}\``,
        `- 補足: ${glb.text}`,
        '- 手順の目安: 画像生成 → image-to-3D（Meshy 等）→ GLB を上記パスへ配置 → ツールの「3D モデル」タブで実在確認・プレビュー',
        {
            aPose: '- プレイアブルと人型敵は A 字型で生成すると自動リギング（アニメーション付け）が安定します',
            scene: '- 静的配置の NPC/敵はリギング不要のため、物語の姿のまま 3D 化して配置すると自然に見えます',
            natural: '- 非人型は接地・シルエットが分かる構図だと 3D 化しやすいです',
        }[pose],
        '',
    ].join('\n');
}
